import type { RouteSectionProps } from "@solidjs/router";
import { A } from "@solidjs/router";
import { Show, createSignal } from "solid-js";

import { Menu } from "~/components";

import { useRIASApp } from "~/_context/UserProvider";

const PublicLayout = (props: RouteSectionProps) => {
	const { isLoggedIn } = useRIASApp();

	// On a phone the menu is folded away behind a button; from md up it is always shown.
	const [menuOpen, setMenuOpen] = createSignal( false );

	// Fold it away again once a link in it has been chosen. Driven by the click rather
	// than by watching the route, so nothing but a tap on the button can change it.
	const foldAfterLink = ( e: MouseEvent ) => {
		if ( ( e.target as HTMLElement ).closest( "a" ) ) setMenuOpen( false );
	};

	return (
		<div class="min-h-screen bg-gray-100 text-gray-900">
			<header class="flex flex-col items-center justify-center min-h-20 bg-white border-gray-400 shadow-md">
				<div class="flex flex-col md:flex-row container mx-auto px-4 md:px-0 py-4 justify-between items-center">
					<div class="flex w-full md:w-auto items-center justify-between">
						<A href="/" class="text-xl font-semibold tracking-tight">
							<img
								src="/images/rias-2026-logo.svg"
								alt="RIAS-regs"
								class="w-auto h-9 md:h-[3.5vh]"
							/>
						</A>

						<button
							type="button"
							class="md:hidden -mr-2 p-2 rounded text-gray-900 hover:bg-gray-100"
							aria-label={menuOpen() ? "Close menu" : "Open menu"}
							aria-expanded={menuOpen()}
							aria-controls="public-nav"
							onClick={() => setMenuOpen( open => !open )}>
							<svg viewBox="0 0 24 24" class="h-7 w-7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
								<Show
									when={menuOpen()}
									fallback={<path d="M4 6h16M4 12h16M4 18h16" />}>
									<path d="M6 6l12 12M18 6L6 18" />
								</Show>
							</svg>
						</button>
					</div>

					<nav
						id="public-nav"
						onClick={foldAfterLink}
						class="w-full md:w-auto flex-col md:flex md:flex-row pt-3 md:py-1 md:pl-4 md:grow md:space-x-4 divide-y divide-gray-100 md:divide-y-0"
						classList={{ flex: menuOpen(), hidden: !menuOpen() }}>
						<Show when={!isLoggedIn()}>
							<A href="/registration/" class="font-semibold py-3 md:py-2">
								Register for Membership
							</A>
						</Show>

						<Menu
							caption="The Scheme"
							items={[
								{ caption: "About the Scheme", href: "/about-the-scheme" },
								{ caption: "Guidance Documents", href: "/guidance-documents/" },
								{ caption: "Definitions", href: "/definitions/" },
								{ caption: "Design Certifiers", href: "/design-certifiers/" },
							]}
						/>

						<Menu
							caption="Membership"
							items={[
								{ caption: "Membership Criteria", href: "/membership-criteria" },
								{ caption: "Common Questions", href: "/membership-faqs/" },
								{ caption: "Terms & Conditions", href: "/terms-and-conditions/" },
							]}
						/>

						<A href="/contact-us/" class="font-semibold py-3 md:py-2">
							Contact Us
						</A>

						<A
							href={isLoggedIn() ? "/dashboard" : "/login/"}
							class="text-blue-600 font-semibold py-3 md:py-2">
							{isLoggedIn() ? "Dashboard" : "Sign in"}
						</A>
					</nav>
				</div>
			</header>

			<main class="container mx-auto py-12 px-6 grid grid-cols-12 gap-4">
				{props.children}
			</main>
		</div>
	);
};

export default PublicLayout;
